/**
 * TourController (v7)
 *
 * Changes from v6:
 *   - Step advance delays reduced from 3000ms to 1500ms for calendar +
 *     wishlist steps. Still enough for the user to see their input land,
 *     but not so long it feels stalled.
 */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { TourContext, STEPS } from '../../contexts/TourContext';
import {
  shouldShowTour,
  getTourState,
  markTourStarted,
  markStepCompleted,
  markTourCompleted,
  markTourSkipped,
  computeBaseline,
  countOf,
} from '../../utils/tourState';
import CoachMark from './CoachMark';
import { TourWelcomeModal, TourCelebrationModal } from './TourWelcomeModal';

const STEP_ADVANCE_DELAY_MS = [0, 0, 1500, 1500];
const STEP_TRANSITION_MS = 300;

const dispatchTourEnded = () => {
  try { window.dispatchEvent(new CustomEvent('worthly:tour-ended')); } catch (e) {}
};

const TourController = ({
  userId,
  userData,
  theme = 'pink',
  rules,
  transactions,
  noSpendDays,
  spendDays,
  neutralDays,
  wishlistItems,
  setActiveTab,
  isBudgetSheetOpen = false,
  isCalendarDialogOpen = false,
  openAddRule,
  openAddTransaction,
  openAddNoSpendDay,
  openAddWishlistItem,
  hasTrialOrPromo = false,
  onStartTrial,
  children,
}) => {
  const [phase, setPhase] = useState('hidden');
  const [currentStep, setCurrentStep] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [stepTransitioning, setStepTransitioning] = useState(false);
  // Pass 12: set the moment a step's completion is detected, so the coach-mark
  // hides immediately even if we're in a delay-before-advance window. Fixes
  // the jarring "coach-mark lingers after user completes the action" issue.
  const [completedStepIndex, setCompletedStepIndex] = useState(-1);

  const advanceQueuedForStepRef = useRef(-1);
  // w2_08: the wait before the next step keeps running even if something
  // else changes in the meantime, so the last pop-up always shows.
  const advanceTimerRef = useRef(null);
  useEffect(() => () => { if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current); }, []);

  useEffect(() => {
    const active = phase === 'welcome' || phase === 'in_progress' || phase === 'celebration';
    try {
      if (typeof window !== 'undefined') {
        window.__worthlyTourActive = active;
        window.dispatchEvent(new CustomEvent('worthly:tour-active-change', { detail: { active } }));
      }
    } catch (e) {}
  }, [phase]);

  const initedRef = useRef(false);
  useEffect(() => {
    if (initedRef.current) return;
    if (!userId || !userData) return;
    initedRef.current = true;

    if (!shouldShowTour(userId)) return;

    const state = getTourState(userId);

    const hasAppUsage =
      countOf(transactions) > 0 ||
      countOf(noSpendDays) > 0 ||
      countOf(spendDays) > 0 ||
      countOf(neutralDays) > 0 ||
      countOf(wishlistItems) > 0;

    if (hasAppUsage && state.status === 'pending') {
      markTourCompleted(userId);
      return;
    }

    if (state.status === 'in_progress' && state.baseline) {
      setBaseline(state.baseline);
      setCurrentStep(state.current_step);
      setPhase('in_progress');
    } else {
      setPhase('welcome');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, !!userData]);

  const notifyTourStateChanged = () => {
    try { window.dispatchEvent(new CustomEvent('worthly:tour-state-changed')); } catch (e) {}
  };

  const handleStart = useCallback(() => {
    const base = computeBaseline({
      rules,
      transactions,
      noSpendDays,
      spendDays,
      neutralDays,
      wishlistItems,
    });
    markTourStarted(userId, base);
    setBaseline(base);
    setCurrentStep(0);
    setPhase('in_progress');
    notifyTourStateChanged();
  }, [userId, rules, transactions, noSpendDays, spendDays, neutralDays, wishlistItems]);

  const handleSkip = useCallback(() => {
    markTourSkipped(userId);
    setPhase('hidden');
    setCurrentStep(null);
    if (setActiveTab) setActiveTab('rules');
    dispatchTourEnded();
    notifyTourStateChanged();
  }, [userId, setActiveTab]);

  const handleFinish = useCallback(() => {
    markTourCompleted(userId);
    setPhase('hidden');
    setCurrentStep(null);
    if (setActiveTab) setActiveTab('rules');
    dispatchTourEnded();
    notifyTourStateChanged();
  }, [userId, setActiveTab]);

  const handleStartTrial = useCallback(() => {
    markTourCompleted(userId);
    setPhase('hidden');
    setCurrentStep(null);
    if (setActiveTab) setActiveTab('rules');
    dispatchTourEnded();
    notifyTourStateChanged();
    setTimeout(() => { if (onStartTrial) onStartTrial(); }, 0);
  }, [userId, setActiveTab, onStartTrial]);

  useEffect(() => {
    if (phase !== 'in_progress') return;
    if (currentStep === null || currentStep >= STEPS.length) return;
    const step = STEPS[currentStep];
    if (setActiveTab) setActiveTab(step.tab);
  }, [phase, currentStep, setActiveTab]);

  useEffect(() => {
    if (phase !== 'in_progress' || currentStep === null) return;
    setStepTransitioning(true);
    const t = setTimeout(() => setStepTransitioning(false), STEP_TRANSITION_MS);
    return () => clearTimeout(t);
  }, [phase, currentStep]);

  useEffect(() => {
    advanceQueuedForStepRef.current = -1;
  }, [currentStep]);

  const handleCoachFallback = useCallback(() => {
    if (currentStep === 0 && openAddRule) openAddRule();
    if (currentStep === 1 && openAddTransaction) openAddTransaction();
    if (currentStep === 2 && openAddNoSpendDay) openAddNoSpendDay();
    if (currentStep === 3 && openAddWishlistItem) openAddWishlistItem();
  }, [currentStep, openAddRule, openAddTransaction, openAddNoSpendDay, openAddWishlistItem]);

  const currentCounts = useMemo(() => ({
    rules: countOf(rules),
    transactions: countOf(transactions),
    calendarDays: countOf(noSpendDays) + countOf(spendDays) + countOf(neutralDays),
    wishlist: countOf(wishlistItems),
  }), [rules, transactions, noSpendDays, spendDays, neutralDays, wishlistItems]);

  useEffect(() => {
    if (phase !== 'in_progress') return;
    if (currentStep === null || currentStep >= STEPS.length) return;
    if (!baseline) return;
    if (advanceQueuedForStepRef.current === currentStep) return;

    const step = STEPS[currentStep];
    const field = step.completionField;

    if (currentCounts[field] > baseline[field]) {
      advanceQueuedForStepRef.current = currentStep;
      // Hide the coach-mark immediately so it doesn't linger while the
      // delay plays out.
      setCompletedStepIndex(currentStep);

      const delay = STEP_ADVANCE_DELAY_MS[currentStep] || 0;
      const advance = () => {
        setBaseline((prev) => (prev ? { ...prev, [field]: currentCounts[field] } : prev));
        setCompletedStepIndex(-1);

        if (currentStep === STEPS.length - 1) {
          setPhase('celebration');
        } else {
          markStepCompleted(userId, currentStep);
          setCurrentStep(currentStep + 1);
        }
      };

      if (delay > 0) {
        if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = setTimeout(() => { advanceTimerRef.current = null; advance(); }, delay);
        return undefined;
      }
      advance();
    }
  }, [phase, currentStep, baseline, currentCounts, userId]);

  const contextValue = useMemo(() => {
    const isTourActive = phase === 'welcome' || phase === 'in_progress' || phase === 'celebration';
    const step = phase === 'in_progress' && currentStep !== null ? STEPS[currentStep] : null;
    return {
      isTourActive,
      isTourMode: isTourActive,
      currentStep: phase === 'in_progress' ? currentStep : null,
      stepName: step?.name || null,
      targetTab: step?.tab || null,
      skipTour: handleSkip,
    };
  }, [phase, currentStep, handleSkip]);

  const coachSuppressed =
    stepTransitioning ||
    completedStepIndex === currentStep ||
    (phase === 'in_progress' &&
      ((currentStep === 1 && isBudgetSheetOpen) ||
        (currentStep === 2 && isCalendarDialogOpen)));

  return (
    <TourContext.Provider value={contextValue}>
      {children}

      {phase === 'welcome' && (
        <TourWelcomeModal
          onStart={handleStart}
          onSkip={handleSkip}
          theme={theme}
        />
      )}

      {phase === 'in_progress' && currentStep !== null && STEPS[currentStep] && (
        <CoachMark
          key={`coach-${currentStep}`}
          targetName={STEPS[currentStep].coachTarget}
          title={STEPS[currentStep].coachTitle}
          body={STEPS[currentStep].coachBody}
          stepIndex={currentStep}
          stepTotal={STEPS.length}
          theme={theme}
          onFallback={handleCoachFallback}
          onSkip={handleSkip}
          suppressed={coachSuppressed}
        />
      )}

      {/* w2_08: nothing can be tapped in the short wait after a step is done */}
      {phase === 'in_progress' && currentStep !== null && completedStepIndex === currentStep && (
        <div
          className="fixed inset-0"
          style={{ zIndex: 10100, background: 'transparent', touchAction: 'none' }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onTouchStart={(e) => e.stopPropagation()}
          data-testid="tour-step-hold"
        />
      )}

      {phase === 'celebration' && (
        <TourCelebrationModal
          onFinish={handleFinish}
          onStartTrial={handleStartTrial}
          theme={theme}
          hasTrialOrPromo={hasTrialOrPromo}
        />
      )}
    </TourContext.Provider>
  );
};

export default TourController;
